import type { MetaTemplate } from '../services/MetaTemplateService';

/**
 * Os rótulos do template da Meta, num lugar só.
 *
 * Vivia duplicado entre a listagem e a página do template, e duplicado é
 * como o rótulo divergia: a tela antiga chamava de "Baixa" uma qualidade
 * que a Meta devolve como `UNKNOWN` (ver `rotuloQualidade`).
 */

export type StatusTemplate = MetaTemplate['status'];

export const STATUS_LABEL: Record<StatusTemplate, string> = {
    APPROVED: 'Aprovado',
    PENDING: 'Pendente',
    IN_REVIEW: 'Em análise',
    REJECTED: 'Rejeitado',
    FLAGGED: 'Sinalizado',
    DISABLED: 'Desabilitado',
    PAUSED: 'Pausado',
};

/** O tom segue o que o status faz com o disparo, não o nome dele. */
export const STATUS_TOM: Record<StatusTemplate, 'is-ok' | 'is-wait' | 'is-bad' | 'is-off'> = {
    APPROVED: 'is-ok',
    PENDING: 'is-wait',
    IN_REVIEW: 'is-wait',
    REJECTED: 'is-bad',
    FLAGGED: 'is-off',
    DISABLED: 'is-off',
    PAUSED: 'is-off',
};

/** Só Aprovado e Rejeitado aceitam alteração na Meta. */
export const EDITAVEL: StatusTemplate[] = ['APPROVED', 'REJECTED'];

export const SEM_NOTA = 'Sem nota';

/**
 * A qualidade **não vem só como HIGH/MEDIUM/LOW**: medido na base em
 * 2026-09-18, os 8 templates voltaram com `qualidade: "UNKNOWN"` — a Meta
 * só pontua depois de entregar um volume de mensagens.
 *
 * A tela antiga não tratava esse valor e caía no último ramo de um ternário
 * (`=== 'HIGH' ? 'Alta' : === 'MEDIUM' ? 'Média' : 'Baixa'`), ou seja
 * **rotulava de "Baixa" todo template sem nota** — e qualidade baixa é o que
 * faz a Meta pausar o template, então o rótulo assustava à toa.
 */
export const rotuloQualidade = (q: string | null | undefined): string => {
    if (q === 'HIGH') return 'Alta';
    if (q === 'MEDIUM') return 'Média';
    if (q === 'LOW') return 'Baixa';
    return SEM_NOTA;
};

/** Classe do ponto de cor. Sem nota não ganha ponto: não há o que pintar. */
export const tomQualidade = (q: string | null | undefined): string | null => {
    if (q === 'HIGH') return '';
    if (q === 'MEDIUM') return 'is-med';
    if (q === 'LOW') return 'is-low';
    return null;
};

/** Para ordenar: a nota é ordinal. Sem nota vai para o fim. */
export const pesoQualidade = (q: string | null | undefined): number => {
    if (q === 'HIGH') return 3;
    if (q === 'MEDIUM') return 2;
    if (q === 'LOW') return 1;
    return 0;
};

export const IDIOMAS = [
    { value: 'pt_BR', label: 'Português (Brasil)' },
    { value: 'en_US', label: 'English (US)' },
    { value: 'es_ES', label: 'Español' },
    { value: 'es_AR', label: 'Español (Argentina)' },
];

export const rotuloIdioma = (v: string) =>
    IDIOMAS.find(i => i.value === v)?.label ?? v;
